import React, { useState } from "react";
import * as analytics from "@/services/analytics";

// External libraries
import { Send } from "lucide-react";
import TextareaAutosize from "react-textarea-autosize";

// UI components
import { Button } from "@/components/ui/button";

// Internal components
import { AttachmentButton } from "./AttachmentButton";
import { RecentDropdown } from "./RecentDropdown";

interface Props {
  onSendMessage?: (message: string) => void;
}

export const ChatInput: React.FC<Props> = ({ onSendMessage }) => {
  const [message, setMessage] = useState("");

  const handleSend = () => {
    if (message.trim()) {
      onSendMessage?.(message);
      analytics.promptEnter(message);
      setMessage("");
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="-mt-4 flex w-full max-w-3xl flex-col gap-3 rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
      {/* Textarea */}
      <div className="flex items-start gap-2">
        <TextareaAutosize
          value={message}
          onChange={e => setMessage(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Ask a non-document grounded question"
          className={`font-inherit flex-1 resize-none border-none text-base leading-6 text-gray-700 outline-none placeholder:text-gray-400 ${
            !message ? "text-center" : ""
          }`}
          minRows={3}
          maxRows={8}
        />
      </div>

      {/* Toolbar */}
      <div className="flex items-center justify-between">
        <div className="flex gap-2">
          <AttachmentButton />

          {/* Recent Button */}
          <RecentDropdown>
            <Button variant="outline" size="sm" className="font-medium">
              Recent
            </Button>
          </RecentDropdown>
        </div>

        {/* Send Button */}
        <Button
          onClick={handleSend}
          disabled={!message.trim()}
          className="h-9 w-9 border-0 bg-coral p-0 text-white transition-colors disabled:opacity-75"
          title="Send message"
        >
          <Send className="h-5 w-5" />
        </Button>
      </div>
    </div>
  );
};
